import { useState } from 'react';
import { Trash2, Plus, Minus, Edit3, User, Package, AlertTriangle, Lock, Gift } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useRepProfile } from '@/lib/RepProfileContext';
import { cur } from '@/lib/currency';
import DocDateField from '@/components/shared/DocDateField';

function fmt(n) {
  return Number(n || 0).toLocaleString('ar-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function OrderLine({ line, onQtyChange, onPriceChange, onDiscountChange, onRemove, allowEditPrice }) {
  const [editPrice, setEditPrice] = useState(false);
  const subtotal = line.quantity * line.price_unit * (1 - (line.discount || 0) / 100);
  // Identify the line by its unique uid when present (promo gift / bundle-split
  // lines can share a template_id), falling back to template_id for normal
  // hand-picked lines. Keeps one-product-per-line behaviour unchanged.
  const lk = line.line_uid ?? line.template_id;
  const isGift = !!line.is_gift;

  return (
    <div className="p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-10 h-10 bg-muted rounded-xl flex items-center justify-center flex-shrink-0">
            <Package className="w-5 h-5 text-muted-foreground/40" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground line-clamp-1">{line.name}</p>
            {isGift && <span className="inline-block text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded mt-0.5">هدية</span>}
            {line.default_code && <p className="text-xs text-muted-foreground">[{line.default_code}]</p>}
          </div>
        </div>
        <button onClick={() => onRemove(lk)}
          className="p-1.5 text-muted-foreground hover:text-red-500 transition-colors flex-shrink-0">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {isGift ? (
          /* Gift line — quantity/price/discount are computed by the promo and
             locked; the rep can only remove it (via the trash button above). */
          <>
            <div className="flex items-center gap-1 text-sm">
              <span className="text-xs text-muted-foreground">الكمية:</span>
              <span className="font-semibold">{line.quantity}</span>
              <Lock className="w-2.5 h-2.5 text-muted-foreground/60" />
            </div>
            <div className="flex items-center gap-1 text-sm flex-1">
              <span className="text-xs text-muted-foreground">سعر:</span>
              <span className="font-medium text-muted-foreground line-through">{fmt(line.price_unit)}</span>
              <span className="text-[11px] font-bold text-emerald-600">مجاناً</span>
            </div>
            <span className="font-bold text-emerald-600 text-sm whitespace-nowrap mr-auto">{fmt(subtotal)} {cur()}</span>
          </>
        ) : (
          <>
        {/* Qty */}
        <div className="flex items-center gap-1">
          <button onClick={() => onQtyChange(lk, Math.max(1, line.quantity - 1))}
            className="w-8 h-8 bg-muted rounded-lg flex items-center justify-center hover:bg-muted/70">
            <Minus className="w-3 h-3" />
          </button>
          <input
            type="number"
            value={line.quantity}
            onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v) && v > 0) onQtyChange(lk, v); }}
            className="w-14 h-8 text-center text-sm font-medium border border-border rounded-lg bg-card focus:outline-none focus:ring-1 focus:ring-primary/50"
          />
          <button onClick={() => onQtyChange(lk, line.quantity + 1)}
            className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center hover:bg-primary/90">
            <Plus className="w-3 h-3 text-white" />
          </button>
        </div>

        {/* Price */}
        <div className="flex items-center gap-1 flex-1">
          <span className="text-xs text-muted-foreground whitespace-nowrap">سعر:</span>
          {editPrice && allowEditPrice ? (
            <input
              type="number"
              value={line.price_unit}
              onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) onPriceChange(lk, v); }}
              onBlur={() => setEditPrice(false)}
              className="w-20 h-7 text-center text-sm border border-primary rounded-lg bg-card focus:outline-none"
              autoFocus
            />
          ) : (
            <button
              onClick={() => allowEditPrice && setEditPrice(true)}
              disabled={!allowEditPrice}
              className={`flex items-center gap-0.5 text-sm font-medium transition-colors ${
                allowEditPrice ? 'hover:text-primary' : 'cursor-not-allowed text-muted-foreground'
              }`}>
              {fmt(line.price_unit)}
              {allowEditPrice
                ? <Edit3 className="w-2.5 h-2.5 text-muted-foreground" />
                : <Lock className="w-2.5 h-2.5 text-muted-foreground/60" />}
            </button>
          )}
        </div>

        {/* Discount */}
        <div className="flex items-center gap-1">
          <span className="text-xs text-muted-foreground">خصم:</span>
          <input
            type="number"
            value={line.discount || 0}
            onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v) && v >= 0 && v <= 100) onDiscountChange(lk, v); }}
            className="w-14 h-7 text-center text-xs border border-border rounded-lg bg-card focus:outline-none focus:ring-1 focus:ring-primary/50"
            min={0} max={100}
          />
          <span className="text-xs text-muted-foreground">%</span>
        </div>

        <span className="font-bold text-primary text-sm whitespace-nowrap mr-auto">{fmt(subtotal)} {cur()}</span>
          </>
        )}
      </div>
    </div>
  );
}

// paymentType / onPaymentTypeChange are only passed where the backend supports
// cash vs credit orders (SellGo's own database); otherwise every order is credit.
export default function OrderReview({ customer, customerInfo, orderLines, onQtyChange, onPriceChange, onDiscountChange, onRemoveLine, onChangeCustomer, onAddMore, onOpenPromos, notes, onNotesChange, onSaveDraft, onConfirm, saving, paymentType, onPaymentTypeChange, invoiceDate, onInvoiceDateChange }) {
  const { profile: repProfile, isManager } = useRepProfile();
  // Managers always edit; reps follow their profile flag.
  const allowEditPrice = isManager || !!repProfile.allow_edit_price;
  const subtotal = orderLines.reduce((s, l) => s + l.quantity * l.price_unit, 0);
  const discountTotal = orderLines.reduce((s, l) => s + (l.quantity * l.price_unit * (l.discount || 0) / 100), 0);
  const afterDiscount = subtotal - discountTotal;
  // Lines only carry a VAT rate when the backend sends one, so Odoo totals are unchanged.
  const taxTotal = orderLines.reduce(
    (s, l) => s + (l.quantity * l.price_unit * (1 - (l.discount || 0) / 100)) * (Number(l.tax_rate) || 0), 0);
  const total = afterDiscount + taxTotal;

  // A cash sale is paid on the spot, so the customer's credit limit doesn't apply.
  const isCredit = (paymentType || 'credit') === 'credit';
  const creditExceeded = isCredit && customerInfo?.credit_limit > 0 && total > (customerInfo?.available_credit || 0);

  return (
    <div className="space-y-4 pb-36">
      {/* Customer */}
      <div className="bg-card border border-border rounded-2xl p-4 flex items-center gap-3">
        <div className="w-11 h-11 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
          <User className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground">العميل</p>
          <p className="font-bold text-foreground truncate">{customer?.name}</p>
          {Number(customer?.credit || 0) > 0 && (
            <span className="inline-flex items-center gap-1 mt-1 text-[11px] font-bold text-red-600 bg-red-50 border border-red-100 px-2 py-0.5 rounded-full">
              مديونية: {Number(customer.credit).toLocaleString('ar-EG', { maximumFractionDigits: 0 })} {cur('ج.م')}
            </span>
          )}
        </div>
        <button onClick={onChangeCustomer} className="text-xs text-primary font-medium">تغيير</button>
      </div>

      {/* Cash or credit */}
      {onPaymentTypeChange && (
        <div className="bg-card border border-border rounded-2xl p-3">
          <p className="text-xs text-muted-foreground mb-2">طريقة الدفع</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              ['cash', 'نقدي', isManager || repProfile.allow_cash
                ? 'العميل يدفع الآن، ويُسجَّل التحصيل تلقائياً' : 'غير مسموح لك بالبيع النقدي'],
              ['credit', 'آجل', 'يُسجَّل المبلغ مديونية على العميل'],
            ].map(([key, label, hint]) => (
              <button
                key={key}
                type="button"
                onClick={() => onPaymentTypeChange(key)}
                disabled={key === 'cash' && !isManager && !repProfile.allow_cash}
                className={`rounded-xl border p-2.5 text-right transition-colors disabled:opacity-50 ${
                  paymentType === key ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                }`}
              >
                <p className={`text-sm font-bold ${paymentType === key ? 'text-primary' : 'text-foreground'}`}>{label}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Invoice date (managers / supervisors on SellGo's own database) */}
      {onInvoiceDateChange && (
        <DocDateField label="تاريخ الفاتورة" value={invoiceDate} onChange={onInvoiceDateChange} />
      )}

      {/* Credit warning */}
      {creditExceeded && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>
            إجمالي الطلب يتجاوز الائتمان المتاح ({Number(customerInfo.available_credit || 0).toLocaleString('ar-EG')} {cur()})
            {onPaymentTypeChange && ' — اختر «نقدي» إذا كان العميل سيدفع الآن'}
          </span>
        </div>
      )}

      {/* Order lines */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="font-bold text-foreground">المنتجات</h3>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{orderLines.filter(l => !l.is_summary).length} صنف</span>
            {onOpenPromos && (
              <button onClick={onOpenPromos} className="text-xs text-emerald-600 font-semibold flex items-center gap-0.5">
                🎁 العروض
              </button>
            )}
            <button onClick={onAddMore} className="text-xs text-primary font-semibold flex items-center gap-0.5">
              <Plus className="w-3 h-3" /> إضافة
            </button>
          </div>
        </div>
        <div className="divide-y divide-border">
          {orderLines.map(line => (
            line.is_summary ? (
              <div key={line.line_uid} className="p-3 bg-emerald-50/60 flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Gift className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-emerald-700 line-clamp-1">باقة: {line.name}</p>
                  <p className="text-[11px] text-muted-foreground">
                    الأصلي <span className="line-through">{fmt(line.orig_total)}</span>
                    {' ← '}الباقة <span className="font-semibold">{fmt(line.bundle_total)}</span> {cur()}
                  </p>
                </div>
                <span className="text-xs font-bold text-emerald-600 whitespace-nowrap">وفّرت {fmt(line.saved)} {cur()}</span>
              </div>
            ) : (
              <OrderLine
                key={line.line_uid ?? line.template_id}
                line={line}
                onQtyChange={onQtyChange}
                onPriceChange={onPriceChange}
                onDiscountChange={onDiscountChange}
                onRemove={onRemoveLine}
                allowEditPrice={allowEditPrice}
              />
            )
          ))}
        </div>
      </div>

      {/* Notes */}
      <div>
        <p className="text-sm font-medium text-foreground mb-1.5">ملاحظات الطلب</p>
        <Textarea value={notes} onChange={e => onNotesChange(e.target.value)}
          placeholder="ملاحظات للمستودع أو التوصيل..." rows={2} className="rounded-xl" />
      </div>

      {/* Totals — sticky bottom */}
      <div className="fixed bottom-0 right-0 left-0 bg-card border-t border-border z-30 px-4 pt-3 pb-4 space-y-2">
        <div className="space-y-1 text-sm">
          <div className="flex justify-between text-muted-foreground">
            <span>المجموع الفرعي</span><span>{fmt(subtotal)} {cur()}</span>
          </div>
          {discountTotal > 0 && (
            <div className="flex justify-between text-muted-foreground">
              <span>الخصم</span><span className="text-red-600">-{fmt(discountTotal)} {cur()}</span>
            </div>
          )}
          {taxTotal > 0 && (
            <div className="flex justify-between text-muted-foreground">
              <span>ضريبة القيمة المضافة</span><span>{fmt(taxTotal)} {cur()}</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-foreground text-base border-t border-border pt-1">
            <span>الإجمالي</span><span className="text-primary">{fmt(total)} {cur()}</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Button onClick={onSaveDraft} variant="outline" disabled={saving} className="rounded-xl h-11">
            حفظ كمسودة
          </Button>
          <Button onClick={onConfirm} disabled={saving || creditExceeded} className="bg-primary rounded-xl h-11">
            {saving ? 'جاري التأكيد...' : 'تأكيد الطلب'}
          </Button>
        </div>
      </div>
    </div>
  );
}